import { AUTO_CAROUSEL_DEBUG_RESULTS_PER_PAGE } from './automated-carousels';
import type { GetCarouselsForSlugParams } from './get-carousels';

export interface PageCarouselsQuery {
  // Comma-separated keywords whose carousels must not appear on the page.
  excludedKeywords?: string | false;
  library: 'music' | 'sfx';
  // The page's name, e.g. "Jazz".
  name: string;
  // Drives the automatic carousels. Empty means the page has none.
  searchTerm?: string;
  // Fetch the larger debug page of automatic carousels.
  showDebugResults?: boolean;
  // The page's path. Empty for collection pages, which have no manual carousels.
  url: string;
}

const processKeywordForFilters = (keyword: string) =>
  keyword.toLocaleLowerCase().trim().replace(/`/g, '').replaceAll(' ', '_');

const initialFilters = (library: string, pageName: string, pageUrl: string) => {
  const filters: string[] = [];

  filters.push(`keywords:[\`${processKeywordForFilters(pageName)}\`]`);

  if (library === 'music') {
    filters.push('asset_type:=`music`');

    filters.push('collection_type:!=`partner`');
    filters.push('name:!=`Premium spotlight`');
    filters.push('name:!`Best of`');
    filters.push('name:!`B-roll essentials`');
  } else if (library === 'sfx') {
    filters.push('asset_type:=`sfx`');
  }

  if (pageName !== 'Christmas') {
    filters.push('keywords:![`Christmas`]');
    filters.push('name:!:=`Christmas`');
  }

  filters.push(`relative_url:!=\`${pageUrl}\``);

  return filters;
};

/**
 * The two searches behind a page's carousels: the ones curated for the page,
 * and the ones matched automatically from its search term.
 */
export const getPageCarouselSearchParameters = ({
  library,
  name,
  url,
  searchTerm = '',
  excludedKeywords,
  showDebugResults = false,
}: PageCarouselsQuery) => {
  const pageSearchParameters: GetCarouselsForSlugParams['searchParameters'] = {
    q: url,
    filter_by: `page_slug:=${url}`,
  };

  const filters = initialFilters(library, name, url);

  if (excludedKeywords) {
    for (const excludedKeyword of excludedKeywords.split(',')) {
      const cleanKeyword = processKeywordForFilters(excludedKeyword);
      if (!cleanKeyword) {
        continue;
      }
      filters.push(`keywords:![\`${cleanKeyword}\`]`);
      filters.push(`name:!:=\`${cleanKeyword}\``);
    }
  }

  const autoSearchParameters: GetCarouselsForSlugParams['searchParameters'] = {
    q: searchTerm,
    num_typos: 0,
    prefix: false,
    query_by: 'embedding',
    filter_by: filters.join(' && '),
    vector_query: 'embedding:([], distance_threshold:1)',
  };

  if (showDebugResults) {
    autoSearchParameters.per_page = AUTO_CAROUSEL_DEBUG_RESULTS_PER_PAGE;
  }

  return { pageSearchParameters, autoSearchParameters };
};
